import {
  createContext,
  useContext,
  useState,
  useRef,
  type ReactNode,
} from "react";
import { useTheme, getColors } from "./theme";
import { useI18n } from "./i18n";

/** Convert index to spreadsheet-style label: 0→A, 25→Z, 26→AA, 27→AB … */
function indexToLabel(n: number): string {
  const letters: string[] = [];
  do {
    letters.unshift(String.fromCharCode(65 + (n % 26)));
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return letters.join("");
}

interface AnonContextValue {
  isAnon: boolean;
  toggle: () => void;
  anonUnit: (id: string | null | undefined, name: string | null | undefined) => string;
  anonHospital: (id: string | null | undefined, name: string | null | undefined) => string;
  anonRegion: (id: string | null | undefined, name: string | null | undefined) => string;
  anonName: (name: string | null | undefined) => string;
  anonDob: (dob: string | null | undefined) => string | null;
}

const AnonContext = createContext<AnonContextValue | null>(null);

export function AnonProvider({ children }: { children: ReactNode }) {
  const [isAnon, setIsAnon] = useState(
    () => localStorage.getItem("biostream-anon") === "1",
  );
  const { t } = useI18n();

  type AnonRef = { map: Map<string, string>; count: number };
  // Stable ID→label maps — refs so additions don't trigger re-renders
  const unitRef = useRef<AnonRef>({ map: new Map(), count: 0 });
  const hospitalRef = useRef<AnonRef>({ map: new Map(), count: 0 });
  const regionRef = useRef<AnonRef>({ map: new Map(), count: 0 });

  const toggle = () => {
    setIsAnon((prev) => {
      const next = !prev;
      localStorage.setItem("biostream-anon", next ? "1" : "0");
      return next;
    });
  };

  const makeAnonFn = (ref: { current: AnonRef }, i18nKey: string) =>
    (id: string | null | undefined, name: string | null | undefined): string => {
      if (!isAnon) return name || "";
      if (!id) return "—";
      if (!ref.current.map.has(id))
        ref.current.map.set(id, indexToLabel(ref.current.count++));
      return t(i18nKey, { label: ref.current.map.get(id)! });
    };

  const anonUnit = makeAnonFn(unitRef, "anon.unit");
  const anonHospital = makeAnonFn(hospitalRef, "anon.hospital");
  const anonRegion = makeAnonFn(regionRef, "anon.region");

  const anonName = (name: string | null | undefined): string =>
    isAnon ? "—" : name || "";

  const anonDob = (dob: string | null | undefined): string | null =>
    isAnon ? null : dob ?? null;

  return (
    <AnonContext.Provider
      value={{ isAnon, toggle, anonUnit, anonHospital, anonRegion, anonName, anonDob }}
    >
      {children}
    </AnonContext.Provider>
  );
}

export function useAnon() {
  const ctx = useContext(AnonContext);
  if (!ctx) throw new Error("useAnon must be used within AnonProvider");
  return ctx;
}

export function AnonSwitcher() {
  const { isAnon, toggle } = useAnon();
  const { theme } = useTheme();
  const colors = getColors(theme);
  return (
    <button
      onClick={toggle}
      title={isAnon ? "Disable anonymous mode" : "Enable anonymous mode"}
      style={{
        padding: "1px 6px",
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        cursor: "pointer",
        background: isAnon ? colors.primary : "transparent",
        color: isAnon ? "#fff" : colors.textMuted,
        fontSize: "0.875rem",
        lineHeight: 1,
      }}
    >
      {"\u{1F576}\uFE0E"}
    </button>
  );
}
